import { useState, type FormEvent } from 'react';
import {
  api,
  camposDoErro,
  mensagemDoErro,
  type EntradaProvisionamento,
  type ProvedorProvisionamento,
} from '../api';
import { Campo, EstadoVazio } from '../componentes/ui';

const FUSOS = [
  { valor: 'America/Sao_Paulo', rotulo: 'Brasília / São Paulo' },
  { valor: 'America/Manaus', rotulo: 'Manaus' },
  { valor: 'America/Belem', rotulo: 'Belém' },
  { valor: 'America/Fortaleza', rotulo: 'Fortaleza' },
  { valor: 'America/Recife', rotulo: 'Recife' },
  { valor: 'America/Bahia', rotulo: 'Salvador' },
  { valor: 'America/Cuiaba', rotulo: 'Cuiabá' },
  { valor: 'America/Campo_Grande', rotulo: 'Campo Grande' },
  { valor: 'America/Porto_Velho', rotulo: 'Porto Velho' },
  { valor: 'America/Rio_Branco', rotulo: 'Rio Branco' },
  { valor: 'America/Noronha', rotulo: 'Fernando de Noronha' },
];

const FORMULARIO_INICIAL: EntradaProvisionamento = {
  slug: '',
  nome: '',
  fuso: 'America/Sao_Paulo',
  janelaInicio: '08:00',
  janelaFim: '20:00',
  adminNome: '',
  adminEmail: '',
  senhaInicial: '',
};

function dataDeCadastro(valor: string): string {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(valor));
}

export function Provisionamento() {
  const [token, setToken] = useState('');
  const [provedores, setProvedores] = useState<ProvedorProvisionamento[]>([]);
  const [formulario, setFormulario] = useState(FORMULARIO_INICIAL);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [mensagem, setMensagem] = useState('');
  const [busca, setBusca] = useState('');
  const [autenticado, setAutenticado] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const conectar = async (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    setCarregando(true);
    setMensagem('');
    try {
      const resposta = await api.listarProvedoresInterno(token);
      setProvedores(resposta.provedores);
      setAutenticado(true);
      setErros({});
    } catch (erro) {
      setMensagem(mensagemDoErro(erro));
      setAutenticado(false);
    } finally {
      setCarregando(false);
    }
  };

  const atualizarCampo = <K extends keyof EntradaProvisionamento>(
    campo: K,
    valor: EntradaProvisionamento[K],
  ) => {
    setFormulario((atual) => ({ ...atual, [campo]: valor }));
  };

  const cadastrar = async (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    if (!autenticado) return;

    setSalvando(true);
    setMensagem('');
    setErros({});
    try {
      await api.criarProvedorInterno(token, {
        ...formulario,
        slug: formulario.slug.trim().toLowerCase(),
        nome: formulario.nome.trim(),
        adminNome: formulario.adminNome.trim(),
        adminEmail: formulario.adminEmail.trim(),
      });
      const resposta = await api.listarProvedoresInterno(token);
      setProvedores(resposta.provedores);
      setFormulario(FORMULARIO_INICIAL);
      setMensagem('Provedor cadastrado. Configure ELO, canal e DNS antes de iniciar a operação.');
    } catch (erro) {
      setMensagem(mensagemDoErro(erro));
      setErros(camposDoErro(erro));
    } finally {
      setSalvando(false);
    }
  };

  const desconectar = () => {
    setToken('');
    setProvedores([]);
    setAutenticado(false);
    setMensagem('');
    setBusca('');
  };

  const provedoresFiltrados = provedores.filter((provedor) => {
    const termo = busca.trim().toLocaleLowerCase('pt-BR');
    return termo === '' ||
      provedor.nome.toLocaleLowerCase('pt-BR').includes(termo) ||
      provedor.slug.toLocaleLowerCase('pt-BR').includes(termo);
  });

  return (
    <main className="pagina provisionamento">
      <header className="pagina__cabecalho">
        <div>
          <p className="provisionamento__sobre">DISPAROS · USO INTERNO</p>
          <h1>Cadastro de Provedores</h1>
          <p className="pagina__descricao">
            Provisionamento de uma Conta Flow por Instância AtenderBem.
          </p>
        </div>
      </header>

      {!autenticado ? (
        <section className="cartao" aria-labelledby="acesso-interno">
          <div className="cartao__cabecalho">
            <div className="cartao__titulo">
              <h2 id="acesso-interno">Acesso interno</h2>
              <p className="cartao__descricao">
                Use o token de provisionamento configurado no ambiente da API. Ele fica apenas na
                memória desta página.
              </p>
            </div>
          </div>
          <form className="provisionamento__formulario" onSubmit={conectar}>
            <Campo rotulo="Token interno" erro={erros.token}>
              <input
                className="entrada"
                type="password"
                autoComplete="current-password"
                value={token}
                onChange={(evento) => setToken(evento.target.value)}
                required
              />
            </Campo>
            {mensagem ? <p className="campo__erro" role="alert">{mensagem}</p> : null}
            <button type="submit" className="botao botao--primario" disabled={carregando || !token}>
              {carregando ? 'Validando…' : 'Acessar backoffice'}
            </button>
          </form>
        </section>
      ) : (
        <>
          <section className="cartao" aria-labelledby="novo-provedor">
            <div className="cartao__cabecalho">
              <div className="cartao__titulo">
                <h2 id="novo-provedor">Cadastrar novo Provedor</h2>
                <p className="cartao__descricao">
                  A conta começa sem canal ou conexão ELO configurados e com todas as Receitas
                  desativadas.
                </p>
              </div>
              <button type="button" className="botao botao--secundario" onClick={desconectar}>
                Encerrar acesso
              </button>
            </div>
            <form className="provisionamento__formulario" onSubmit={cadastrar}>
              <div className="grade grade--2">
                <Campo
                  rotulo="Nome do Provedor"
                  erro={erros.nome}
                >
                  <input
                    className="entrada"
                    autoComplete="organization"
                    value={formulario.nome}
                    onChange={(evento) => atualizarCampo('nome', evento.target.value)}
                    required
                  />
                </Campo>
                <Campo
                  rotulo="Identificador da instância"
                  ajuda="Minúsculas, números e hífen. Deve corresponder ao subdomínio configurado no DNS."
                  erro={erros.slug}
                >
                  <input
                    className="entrada mono"
                    autoComplete="off"
                    minLength={2}
                    maxLength={40}
                    pattern="[a-z0-9](?:[a-z0-9-]*[a-z0-9])?"
                    value={formulario.slug}
                    onChange={(evento) =>
                      atualizarCampo('slug', evento.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                    }
                    required
                  />
                </Campo>
                <Campo rotulo="Fuso horário" erro={erros.fuso}>
                  <select
                    className="entrada"
                    value={formulario.fuso}
                    onChange={(evento) => atualizarCampo('fuso', evento.target.value)}
                  >
                    {FUSOS.map((fuso) => (
                      <option key={fuso.valor} value={fuso.valor}>{fuso.rotulo}</option>
                    ))}
                  </select>
                </Campo>
                <div className="grade grade--2">
                  <Campo rotulo="Início da Janela de Envio" erro={erros.janelaInicio}>
                    <input
                      className="entrada"
                      type="time"
                      value={formulario.janelaInicio}
                      onChange={(evento) => atualizarCampo('janelaInicio', evento.target.value)}
                      required
                    />
                  </Campo>
                  <Campo rotulo="Fim da Janela de Envio" erro={erros.janelaFim}>
                    <input
                      className="entrada"
                      type="time"
                      value={formulario.janelaFim}
                      onChange={(evento) => atualizarCampo('janelaFim', evento.target.value)}
                      required
                    />
                  </Campo>
                </div>
              </div>

              <h3 className="provisionamento__subtitulo">Administrador inicial</h3>
              <div className="grade grade--2">
                <Campo rotulo="Nome" erro={erros.adminNome}>
                  <input
                    className="entrada"
                    autoComplete="name"
                    value={formulario.adminNome}
                    onChange={(evento) => atualizarCampo('adminNome', evento.target.value)}
                    required
                  />
                </Campo>
                <Campo rotulo="E-mail de acesso" erro={erros.adminEmail}>
                  <input
                    className="entrada"
                    type="email"
                    autoComplete="email"
                    value={formulario.adminEmail}
                    onChange={(evento) => atualizarCampo('adminEmail', evento.target.value)}
                    required
                  />
                </Campo>
                <Campo
                  rotulo="Senha inicial"
                  ajuda="Mínimo de 12 caracteres. Compartilhe por um canal seguro."
                  erro={erros.senhaInicial}
                >
                  <input
                    className="entrada"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={128}
                    value={formulario.senhaInicial}
                    onChange={(evento) => atualizarCampo('senhaInicial', evento.target.value)}
                    required
                  />
                </Campo>
              </div>

              {mensagem ? (
                <p className={erros.senhaInicial || erros.slug ? 'campo__erro' : 'provisionamento__sucesso'} role="status">
                  {mensagem}
                </p>
              ) : null}
              <button type="submit" className="botao botao--primario" disabled={salvando}>
                {salvando ? 'Cadastrando…' : 'Cadastrar Provedor'}
              </button>
            </form>
          </section>

          <section className="cartao" aria-labelledby="provedores-cadastrados">
            <div className="cartao__cabecalho">
              <div className="cartao__titulo">
                <h2 id="provedores-cadastrados">Provedores cadastrados</h2>
                <p className="cartao__descricao">
                  {provedoresFiltrados.length} de {provedores.length} contas Flow provisionadas.
                </p>
              </div>
            </div>
            {provedores.length === 0 ? (
              <EstadoVazio
                titulo="Nenhum Provedor cadastrado"
                texto="Cadastre o primeiro Provedor pelo formulário acima."
                icone="⌂"
              />
            ) : (
              <div className="pilha">
                <Campo rotulo="Buscar por Provedor ou slug">
                  <input
                    className="entrada"
                    type="search"
                    value={busca}
                    onChange={(evento) => setBusca(evento.target.value)}
                  />
                </Campo>
                {provedoresFiltrados.length === 0 ? (
                  <EstadoVazio
                    titulo="Nenhum resultado"
                    texto="Revise o nome do Provedor ou o slug informado."
                    icone="⌕"
                  />
                ) : (
                  <div className="tabela-rolagem">
                    <table className="provisionamento__tabela">
                      <caption className="sr-apenas">Contas Flow provisionadas</caption>
                      <thead>
                        <tr>
                          <th scope="col">Provedor</th>
                          <th scope="col">Instância / slug</th>
                          <th scope="col">Situação</th>
                          <th scope="col">Criado em</th>
                        </tr>
                      </thead>
                      <tbody>
                        {provedoresFiltrados.map((provedor) => (
                          <tr key={provedor.id}>
                            <th scope="row">{provedor.nome}</th>
                            <td><code>{provedor.slug}</code></td>
                            <td>{provedor.ativo ? 'Ativo' : 'Inativo'}</td>
                            <td>{dataDeCadastro(provedor.criadoEm)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>
        </>
      )}
    </main>
  );
}
